import { FC, useState, useEffect, useRef, useCallback } from 'react';
import { profileService } from '@/services/profile.service';

interface NotificationTypeConfig {
  key: string;
  label: string;
  description: string;
  icon: string;
  disabled?: boolean;
  tooltip?: string;
}

const NOTIFICATION_TYPES: NotificationTypeConfig[] = [
  { key: 'LEVEL_UP', label: 'Subida de Nível', description: 'Quando você sobe de nível', icon: 'trending_up' },
  { key: 'ACHIEVEMENT', label: 'Conquista de Badge', description: 'Quando você desbloqueia um badge', icon: 'emoji_events' },
  { key: 'TRAIL_COMPLETED', label: 'Curso Finalizado', description: 'Quando você completa um curso', icon: 'school' },
  { key: 'MISSION_COMPLETED', label: 'Missão Cumprida', description: 'Quando você completa uma missão', icon: 'flag' },
  { key: 'RANKING_CHANGE', label: 'Mudança no Ranking', description: 'Quando sua posição no ranking muda', icon: 'leaderboard' },
  { key: 'NEW_CONTENT', label: 'Novo Conteúdo', description: 'Quando novos cursos são publicados', icon: 'library_books' },
  { key: 'SYSTEM', label: 'Avisos do Sistema', description: 'Notificações do sistema não podem ser desativadas', icon: 'campaign', disabled: true, tooltip: 'Notificações do sistema não podem ser desativadas' },
];

const NotificationPreferencesSection: FC = () => {
  const [preferences, setPreferences] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const debounceTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    profileService.getNotificationPreferences()
      .then((prefs) => setPreferences(prefs))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  // Clear toast after 2 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const handleToggle = useCallback((key: string) => {
    const newValue = !preferences[key];
    setPreferences((prev) => ({ ...prev, [key]: newValue }));

    // Clear existing debounce timer for this key
    if (debounceTimers.current[key]) {
      clearTimeout(debounceTimers.current[key]);
    }

    debounceTimers.current[key] = setTimeout(async () => {
      setSavingKey(key);
      try {
        await profileService.updateNotificationPreferences({ [key]: newValue });
        setToast('Preferência salva');
      } catch {
        // Revert on error
        setPreferences((prev) => ({ ...prev, [key]: !newValue }));
        setToast('Erro ao salvar preferência');
      } finally {
        setSavingKey(null);
      }
    }, 500);
  }, [preferences]);

  // Cleanup timers on unmount
  useEffect(() => {
    const timers = debounceTimers.current;
    return () => {
      Object.values(timers).forEach(clearTimeout);
    };
  }, []);

  if (isLoading) {
    return (
      <section className="p-6 bg-surface-card border border-theme-border rounded-xl" data-testid="notification-preferences-section">
        <div className="flex items-center gap-3 mb-4">
          <span className="material-symbols-outlined text-content-muted">notifications</span>
          <h2 className="text-lg font-bold text-content-primary">Preferências de Notificação</h2>
        </div>
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-12 bg-surface-tertiary rounded-lg" />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="p-6 bg-surface-card border border-theme-border rounded-xl" data-testid="notification-preferences-section">
      <div className="flex items-center gap-3 mb-4">
        <span className="material-symbols-outlined text-content-muted">notifications</span>
        <h2 className="text-lg font-bold text-content-primary">Preferências de Notificação</h2>
      </div>
      <p className="text-sm text-content-muted mb-4">Escolha quais tipos de notificação você deseja receber.</p>

      <div className="space-y-1">
        {NOTIFICATION_TYPES.map((type) => {
          const isEnabled = type.key === 'SYSTEM' ? true : (preferences[type.key] !== false);
          const isSaving = savingKey === type.key;

          return (
            <div
              key={type.key}
              className="flex items-center justify-between py-3 px-3 rounded-lg hover:bg-surface-secondary transition-colors"
              data-testid={`notification-pref-${type.key}`}
            >
              <div className="flex items-center gap-3">
                <span className="material-symbols-outlined text-content-muted text-xl">{type.icon}</span>
                <div>
                  <p className="text-sm font-medium text-content-primary">{type.label}</p>
                  <p className="text-xs text-content-muted">{type.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isSaving && (
                  <span className="text-xs text-content-muted" data-testid={`saving-${type.key}`}>Salvando...</span>
                )}
                <button
                  onClick={() => handleToggle(type.key)}
                  disabled={type.disabled || isSaving}
                  data-testid={`toggle-${type.key}`}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    isEnabled ? 'bg-accent' : 'bg-surface-tertiary'
                  } ${type.disabled ? 'opacity-50 cursor-not-allowed' : ''} ${isSaving ? 'opacity-70' : ''}`}
                  role="switch"
                  aria-checked={isEnabled}
                  aria-label={type.label}
                  title={type.tooltip}
                >
                  <span
                    className={`inline-block size-4 rounded-full bg-white transition-transform ${
                      isEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Toast */}
      {toast && (
        <div
          className={`fixed bottom-6 right-6 px-4 py-2 rounded-lg text-sm font-medium text-white shadow-lg transition-opacity z-50 ${
            toast.includes('Erro') ? 'bg-red-500' : 'bg-green-600'
          }`}
          data-testid="notification-pref-toast"
        >
          {toast}
        </div>
      )}
    </section>
  );
};

export default NotificationPreferencesSection;
